import type { ChartOptions } from 'chart.js';
import type { ChartProps } from '../types/chart.types';
import 'chart.js/auto';
import { Box } from '@mui/material';
import React from 'react';
import { Line } from 'react-chartjs-2';

// TODO Refactor to make it more reuseable

interface TooltipMeta {
  [date: string]: Array<{
    companyName: string;
    reviewDepth: string;
    completed: boolean;
  }>;
}

type Props = ChartProps & {
  tooltipMeta?: TooltipMeta;
};

function LineChart({
  labels,
  data,
  labelText = 'Review Activity',
  animateChart = true,
  disableTooltip = false,
  disableAnimation = false,
  tooltipMeta = {},
}: Props) {
  const chartData = {
    labels,
    datasets: [
      {
        label: labelText,
        data,
        borderColor: '#2c598e',
        backgroundColor: 'rgba(44, 89, 142, 0.1)',
        fill: true,
        tension: 0.3,
        pointRadius: 5,
        pointHoverRadius: 7,
        pointBackgroundColor: '#2c598e',
      },
    ],
  };

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: { font: { size: 12 } },
      },
      tooltip: disableTooltip
        ? { enabled: false }
        : {
            backgroundColor: '#fff',
            borderColor: '#ccc',
            borderWidth: 1,
            titleColor: '#333',
            bodyColor: '#333',
            titleFont: { weight: 'bold' },
            padding: 10,
            callbacks: {
              title: (items) => {
                const raw = items[0].label;
                const date = new Date(raw ?? '');
                return `Date: ${date.toLocaleDateString(undefined, {
                  month: 'short',
                  day: 'numeric',
                })}`;
              },
              label: () => '',
              afterLabel: (ctx) => {
                const date = ctx.label;
                const rows = tooltipMeta[date] ?? [];
                return rows.map(
                  (r: { companyName: string; reviewDepth: string; completed: boolean }) =>
                    `${r.companyName} • ${r.reviewDepth} • ${r.completed ? '✅ Completed' : '🕗 In Progress'}`,
                );
              },
            },
          },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          color: '#666',
          font: { size: 12 },
          callback: (val: any) => {
            const label = labels[val];
            return new Date(label).toLocaleDateString(undefined, {
              month: 'short',
              day: 'numeric',
            });
          },
        },
      },
      y: {
        beginAtZero: true,
        grid: { display: false },
        ticks: {
          stepSize: 1,
          color: '#666',
          font: { size: 12 },
        },
      },
    },
    animation: disableAnimation
      ? { duration: 0 }
      : animateChart
        ? { duration: 600, easing: 'easeOutQuart' }
        : { duration: 0 },
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '360px',
        padding: '0 24px 12px 24px',
        boxSizing: 'border-box',
      }}
    >
      <Line data={chartData} options={options} />
    </Box>
  );
}

export default LineChart;
